import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Input, Loader, Table } from '@itds/elements/index.js';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMembers,
  setCurrentPage,
  setSearchQuery,
  clearCache
} from '@redux/brokerageSlices/memberSlice';

import AddMember from './AddMember';
import { AgencyContext } from '@brokerage/contexts/AgencyContext';
import Avatar from '@brokerage/components/Avatar';
import ImportMembers from './ImportMembers';
import NewDrawer from '@brokerage/pages/members/New';
import { debounce } from 'lodash';
import DOMPurify from 'dompurify';

const List = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const context = useContext(AgencyContext);

  const { members, pagination, loading, currentPage, searchQuery, error } = useSelector(
    state => state.members
  );

  const [localSearchTerm, setLocalSearchTerm] = useState(searchQuery);
  const [showNew, toggleAddMember] = useState(false);
  const [showImport, toggleImportMembers] = useState(false);
  const [currentAgencyMemberId, setCurrentAgencyMember] = useState(null);

  useEffect(() => {
    dispatch(fetchMembers({ agency_id: params.agency_id, page: currentPage, search: searchQuery }));
  }, [dispatch, params.agency_id, currentPage, searchQuery]);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce(value => {
        dispatch(setSearchQuery(value));
      }, 500),
    [dispatch]
  );

  const handleSearchChange = e => {
    let value = e.target.value.trimStart();

    value = DOMPurify.sanitize(value);

    setLocalSearchTerm(value);
    debouncedSetSearchTerm(value);
  };

  const handleSearchKeyDown = e => {
    if (e.keyCode === 13) {
      debouncedSetSearchTerm.cancel();
      dispatch(setSearchQuery(localSearchTerm));
      dispatch(setCurrentPage(1));
      dispatch(clearCache());
    }
  };

  const handlePageChange = newPage => {
    dispatch(setCurrentPage(newPage));
    dispatch(fetchMembers({ agency_id: params.agency_id, page: newPage, search: searchQuery }));
  };

  const handleNewMember = () => {
    toggleAddMember(false);
    dispatch(fetchMembers({ agency_id: params.agency_id, page: currentPage, search: searchQuery }));
  };

  const handleUnexistMember = savedData => {
    const data_ = savedData.data;

    handleCloseNewMember();
    setCurrentAgencyMember({
      value: data_.id,
      label: `${data_.email} (${data_.first_name} ${data_.last_name})`
    });
    context.loadMembers();
  };

  const handleCloseNewMember = () => {
    context.setNestedNewMember(false);
    toggleAddMember(true);
  };

  return (
    <>
      <ImportMembers
        members={context.non_agency_members}
        onClose={() => toggleImportMembers(false)}
        onSave={handleNewMember}
        show={showImport}
      />

      <AddMember
        currentAgencyMember={currentAgencyMemberId}
        members={context.non_agency_members}
        onClose={() => toggleAddMember(false)}
        onSave={handleNewMember}
        show={showNew}
      />

      <NewDrawer
        onClose={handleCloseNewMember}
        onSave={handleUnexistMember}
        show={context.nestedNewMember}
      />

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
        <Input
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          placeholder='Search by name or email...'
          style={{ width: 300 }}
          value={localSearchTerm}
        />

        <div style={{ marginLeft: 'auto' }}>
          <Button onClick={() => toggleImportMembers(true)}>Import Members</Button>
        </div>
        <div style={{ marginLeft: '4px' }}>
          <Button onClick={() => toggleAddMember(true)}>Add Member</Button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <Table
          apiPagination={{ ...pagination, current_page: currentPage, onClick: handlePageChange }}
          columns={[
            {
              id: 'image',
              label: '',
              width: 50,
              render: member => <Avatar image={member.image.url} />,
              styles: { padding: '30px 15px', verticalAlign: 'top' }
            },
            {
              id: 'name',
              label: 'Name',
              styles: { padding: '30px 15px', verticalAlign: 'top' },
              render: member => `${member.first_name} ${member.last_name}`
            },
            {
              id: 'email',
              label: 'Email',
              styles: { padding: '30px 15px', verticalAlign: 'top' }
            },
            {
              id: 'role',
              label: 'Role',
              styles: { padding: '30px 15px', verticalAlign: 'top' },
              render: member => {
                const agencyId = parseInt(params.agency_id, 10);
                const role = member.member_agency_roles.find(r => r.agency_id === agencyId);

                return role ? role.name : 'N/A';
              }
            }
          ]}
          data={members}
          onRowClick={member => {
            navigate(`/members/${member.id}/general`);
          }}
        />
      )}
    </>
  );
};

export default List;
